import { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { useAuthStore } from "../stores/authStore";
import { submitSupportRequest, type SupportContactRequest } from "../services/supportService";

type IssueType = "bug" | "feature_request" | "question" | "account" | "other";
type ContactMethod = "email" | "phone" | "either";

const ISSUE_TYPES: { value: IssueType; label: string; icon: string }[] = [
  { value: "bug", label: "Bug Report", icon: "🐛" },
  { value: "feature_request", label: "Feature Request", icon: "💡" },
  { value: "question", label: "Question", icon: "❓" },
  { value: "account", label: "Account Issue", icon: "👤" },
  { value: "other", label: "Other", icon: "📝" },
];

const CONTACT_METHODS: { value: ContactMethod; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "either", label: "Either" },
];

export function SupportPage() {
  const { user } = useAuthStore();
  
  // Form state
  const [name, setName] = useState(user?.display_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [preferredContact, setPreferredContact] = useState<ContactMethod>("email");
  const [issueType, setIssueType] = useState<IssueType>("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const request: SupportContactRequest = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        preferred_contact: preferredContact,
        issue_type: issueType,
        subject: subject.trim(),
        message: message.trim(),
        user_id: user?.id,
      };

      await submitSupportRequest(request);
      setSuccess(true);
      
      // Reset form
      if (!user) {
        setName("");
        setEmail("");
      }
      setPhone("");
      setPreferredContact("email");
      setIssueType("question");
      setSubject("");
      setMessage("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div>
        <PageHeader
          title="Contact Support"
          subtitle="Need help? Fill out the form below and we'll get back to you."
        />
        <div className="px-4 sm:px-6 lg:px-8 pb-8 max-w-2xl mx-auto">
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-surface-100 mb-2">Request Submitted!</h2>
            <p className="text-surface-400 mb-6">
              Thank you for reaching out. Our support team will get back to you as soon as possible.
            </p>
            <button
              onClick={() => setSuccess(false)}
              className="px-6 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Contact Support"
        subtitle="Need help? Fill out the form below and we'll get back to you."
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Contact Information
            </h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-surface-300 mb-1">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-accent-500"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-surface-300 mb-1">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-accent-500"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-surface-300 mb-1">
                  Phone Number <span className="text-surface-500">(optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-accent-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Preferred Contact Method
                </label>
                <div className="flex gap-3">
                  {CONTACT_METHODS.map((method) => (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() => setPreferredContact(method.value)}
                      className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                        preferredContact === method.value
                          ? "border-accent-500 bg-accent-500/20 text-accent-400"
                          : "border-surface-700 bg-surface-800 text-surface-300 hover:bg-surface-700"
                      }`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Issue Details */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Issue Details
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  What type of issue is this? <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ISSUE_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setIssueType(type.value)}
                      className={`py-3 px-4 rounded-lg border text-sm font-medium transition-colors flex flex-col items-center gap-1 ${
                        issueType === type.value
                          ? "border-accent-500 bg-accent-500/20 text-accent-400"
                          : "border-surface-700 bg-surface-800 text-surface-300 hover:bg-surface-700"
                      }`}
                    >
                      <span className="text-lg">{type.icon}</span>
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-surface-300 mb-1">
                  Subject <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-accent-500"
                  placeholder="Brief summary of your issue"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-surface-300 mb-1">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                  className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-accent-500 resize-none"
                  placeholder="Please describe your issue in detail. Include any relevant information that might help us assist you better."
                />
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 bg-accent-500 text-white font-medium rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Message
              </>
            )}
          </button>

          <p className="text-center text-sm text-surface-500">
            Your message will be sent to support@colvix.net
          </p>
        </form>
      </div>
    </div>
  );
}
